import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/Modal";
import Menu, { type MenuItem } from "../../components/Menu";
import api from "../../utils/api";
import { alprService } from "../../utils/api";
import Pagination from "../../components/Pagination";
import { usePagination } from "../../hooks/usePagination";
import { PaginationParams, PageResponse, ApiResponse } from "../../types";
import "./ALPR.css";

interface AlprData {
  alprId: string;
  deviceId: string;
  lotId: string;
  plateNumber: string;
  timestamp: string;
  confidence: number;
  vehicleMake: string;
  vehicleModel: string;
  vehicleMakeModelConfidence: number;
  vehicleState: string;
  vehicleStateConfidence: number;
  vehicleRegion: string;
  vehicleStatus: string;
  laneType: string;
  distanceChange: number;
  vehicleColor: string;
  vehicleColorConfidence: number;
  plateColor: string;
  plateCharacterColor: string;
  vehicleView: string;
  vehicleViewConfidence: number;
  fullImage: string;
  decisionReason: string;
}

interface Device {
  deviceId: string;
  deviceDisplayId: string;
  lotId: string;
  deviceType: string;
  deviceStatus: string;
}

type SortDirection = "ascending" | "descending";
type SortKey =
  | "plateNumber"
  | "timestamp"
  | "vehicleStatus"
  | "decisionReason"
  | "confidence"
  | "vehicleState";

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

interface NewEntryState {
  plateNumber: string;
  timestamp: string;
  region: string;
  vehicleStatus: "Enter" | "Exit" | "Idle";
  idleLaneType?: "Enter" | "Exit";
}

const TABLE_PAGE_SIZE = 15;

const extractStateFromVehicleState = (vehicleState: string | undefined) => {
  if (!vehicleState) return "Unknown";
  const dashIndex = vehicleState.indexOf(" - ");
  return dashIndex !== -1 ? vehicleState.substring(dashIndex + 3).trim() : vehicleState;
};

const formatTimestampDisplay = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const safeDiff = Math.max(diffMs, 0);
  const seconds = Math.floor(safeDiff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const timeText = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (seconds < 60) {
    const secondValue = Math.max(seconds, 1);
    const label = `${secondValue} second${secondValue === 1 ? "" : "s"} ago`;
    return { primary: label, secondary: timeText };
  }

  if (minutes < 60) {
    const label = `${minutes} min${minutes === 1 ? "" : "s"} ago`;
    return { primary: label, secondary: timeText };
  }

  if (hours < 3) {
    const remainingMinutes = minutes % 60;
    const label = remainingMinutes
      ? `${hours}h ${remainingMinutes}m ago`
      : `${hours} hour${hours === 1 ? "" : "s"} ago`;
    return { primary: label, secondary: timeText };
  }

  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  if (isSameDay) {
    return { primary: "Today", secondary: timeText };
  }

  if (isYesterday) {
    return { primary: "Yesterday", secondary: timeText };
  }

  const dateText = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return { primary: dateText, secondary: timeText };
};

const formatDisplayLotId = (id: string) => {
  if (!id || id.length < 8) return id;
  const lastEight = id.slice(-8);
  return `${lastEight.slice(0, 4)}-${lastEight.slice(4)}`;
};

const ALPR: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "timestamp",
    direction: "descending",
  });
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AlprData | null>(null);
  const [newEntry, setNewEntry] = useState<NewEntryState>({
    plateNumber: "",
    timestamp: new Date().toLocaleString("sv-SE").slice(0, 16),
    region: "Test",
    vehicleStatus: "Enter",
    idleLaneType: "Enter",
  });
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [lotDisplayId, setLotDisplayId] = useState("");
  const [downloadFilename, setDownloadFilename] = useState("");
  const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const copyToClipboard = useCallback((value?: string) => {
    if (!value) return;
    const fallbackCopy = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (error) {
        console.error("Failed to copy text", error);
      }
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  }, []);
  const handleCopy = useCallback(
    (value?: string, key?: string) => {
      if (!value || !key) return;
      copyToClipboard(value);
      setCopiedKey(key);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setCopiedKey(null), 500);
    },
    [copyToClipboard]
  );

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window === "undefined") {
        setIsMobile(false);
        return;
      }
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const statusMenuItems: MenuItem[] = useMemo(() => {
    const options: NewEntryState["vehicleStatus"][] = ["Enter", "Exit", "Idle"];
    return options.map((status) => ({
      key: status,
      label: status,
      selected: newEntry.vehicleStatus === status,
      onSelect: () =>
        setNewEntry((prev) => ({
          ...prev,
          vehicleStatus: status,
          idleLaneType: status === "Idle" ? prev.idleLaneType || "Enter" : "Enter",
        })),
    }));
  }, [newEntry.vehicleStatus]);

  const laneMenuItems: MenuItem[] = useMemo(() => {
    const options: Array<"Enter" | "Exit"> = ["Enter", "Exit"];
    return options.map((option) => ({
      key: option,
      label: option,
      selected: newEntry.idleLaneType === option,
      onSelect: () =>
        setNewEntry((prev) => ({
          ...prev,
          idleLaneType: option,
        })),
    }));
  }, [newEntry.idleLaneType]);

  const getDefaultFilename = useCallback(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const formattedLotId = formatDisplayLotId(lotDisplayId);
    return `${formattedLotId || lotId || "LOT"}_VehicleLog_${yyyy}-${mm}-${dd}.csv`;
  }, [lotDisplayId, lotId]);

  useEffect(() => {
    setDownloadFilename(getDefaultFilename());
  }, [getDefaultFilename]);

  const fetchDevicesForLot = useCallback(async (): Promise<Device[]> => {
    if (!lotId) return [];
    try {
      const response = await api.get(`/devices/get-by-lot/${lotId}`);
      const payload = response.data as ApiResponse<Device[]>;
      const lotDevices = Array.isArray(payload?.data) ? [...payload.data] : [];
      lotDevices.sort((a, b) => a.deviceId.localeCompare(b.deviceId));
      return lotDevices;
    } catch (error) {
      console.error("Error fetching devices for lot:", error);
      return [];
    }
  }, [lotId]);

  const fetchLotDisplayId = useCallback(async (): Promise<string> => {
    if (!lotId) return "";
    try {
      const response = await api.get(`/parkinglots/get-by-id/${lotId}`);
      const lotData = response.data;
      const resolvedDisplayId = lotData?.data?.displayLotId || lotId || "";
      setLotDisplayId(resolvedDisplayId);
      return resolvedDisplayId;
    } catch (error) {
      console.error("Error fetching lot display id:", error);
      const fallback = lotId || "";
      setLotDisplayId(fallback);
      return fallback;
    }
  }, [lotId]);

  const fetchAlprPage = useCallback(
    async (params: PaginationParams): Promise<PageResponse<AlprData>> => {
      if (!lotId) {
        return {
          content: [],
          page: 0,
          size: params.size,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true,
          sortBy: params.sortBy,
          sortDirection: params.sortDirection,
        };
      }
      return alprService.getAlprByLotOffsetPaginated(lotId, params);
    },
    [lotId]
  );

  const {
    items: alprEntries,
    totalElements,
    totalPages,
    page,
    loading: paginationLoading,
    sortBy,
    sortDirection,
    setSort,
    setSearch,
    goToPage,
    refresh,
  } = usePagination<AlprData>({
    fetchFn: fetchAlprPage,
    initialPage: 0,
    initialSize: TABLE_PAGE_SIZE,
    initialSortBy: "timestamp",
    initialSortDirection: "desc",
    enabled: !!lotId,
  });

  useEffect(() => {
    if (!lotId) return;
    fetchLotDisplayId();
    refresh();
  }, [lotId, fetchLotDisplayId, refresh]);

  const isLoading = paginationLoading;

  useEffect(() => {
    if (!sortBy) return;
    setSortConfig({
      key: sortBy as SortKey,
      direction: sortDirection === "asc" ? "ascending" : "descending",
    });
  }, [sortBy, sortDirection]);

  useEffect(() => {
    const shouldLock =
      showManualEntryModal ||
      showDownloadModal ||
      showClearConfirmModal ||
      showDetailModal;
    document.body.style.overflow = shouldLock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showManualEntryModal, showDownloadModal, showClearConfirmModal, showDetailModal]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSelectedEntries(new Set());
    setSearchQuery(query);
    setSearch(query);
    goToPage(0);
  };

  const handleSort = (key: SortKey) => {
    const targetDirection =
      sortConfig.key === key && sortConfig.direction === "descending"
        ? "ascending"
        : "descending";
    setSortConfig({ key, direction: targetDirection });
    setSort(key, targetDirection === "descending" ? "desc" : "asc");
    goToPage(0);
  };

  const filteredResults = alprEntries;
  const paginatedEntries = alprEntries;

  const getUniqueKey = (entry: AlprData, index: number) => {
    if (entry.alprId) {
      return `${entry.alprId}-${index}`;
    }
    return `${entry.plateNumber}-${entry.timestamp}-${index}`;
  };

  const handleCheckboxChange = (entry: AlprData, index: number) => {
    const key = getUniqueKey(entry, index);
    setSelectedEntries((prev) => {
      const updated = new Set(prev);
      if (updated.has(key)) {
        updated.delete(key);
      } else {
        updated.add(key);
      }
      return updated;
    });
  };

  const handleSelectAll = () => {
    if (!filteredResults.length) return;
    const allSelected =
      selectedEntries.size === filteredResults.length &&
      filteredResults.length > 0;
    if (allSelected) {
      setSelectedEntries(new Set());
    } else {
      const keys = filteredResults.map((entry, index) =>
        getUniqueKey(entry, index)
      );
      setSelectedEntries(new Set(keys));
    }
  };

  const getSelectedEntriesForDownload = () => {
    if (selectedEntries.size === 0) {
      return filteredResults;
    }
    return filteredResults.filter((entry, index) =>
      selectedEntries.has(getUniqueKey(entry, index))
    );
  };

  const handleOpenDownloadModal = () => {
    setDownloadFilename(getDefaultFilename());
    setShowDownloadModal(true);
  };

  const handleDownload = (filename: string) => {
    const safeFilename = filename.trim() || getDefaultFilename();
    const header =
      "PlateNumber,DateTime,VehicleState,Confidence,VehicleMake,VehicleModel,VehicleStatus,LaneType,DistanceChange,VehicleColour,VehicleColourConfidence,PlateColour,PlateCharacterColour,VehicleRegion,VehicleView,VehicleViewConfidence\n";
    const entries = getSelectedEntriesForDownload();
    const rows = entries.map((entry) => {
      return `${entry.plateNumber},${new Date(entry.timestamp).toISOString()},${entry.vehicleState || ""},${entry.confidence || 0},${entry.vehicleMake || ""},${entry.vehicleModel || ""},${entry.vehicleStatus || ""},${entry.laneType || ""},${entry.distanceChange || ""},${entry.vehicleColor || ""},${entry.vehicleColorConfidence || ""},${entry.plateColor || ""},${entry.plateCharacterColor || ""},${entry.vehicleRegion || ""},${entry.vehicleView || ""},${entry.vehicleViewConfidence || ""}`;
    });
    const blob = new Blob([header + rows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = safeFilename.endsWith(".csv")
      ? safeFilename
      : `${safeFilename}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    setShowDownloadModal(false);
  };

  const handleAddEntry = async () => {
    if (isSubmittingEntry || !lotId) return;
    const { plateNumber, timestamp, region, vehicleStatus, idleLaneType } = newEntry;
    if (!plateNumber) {
      alert("Please enter a plate number.");
      return;
    }
    setIsSubmittingEntry(true);

    try {
      const resolvedDisplayId =
        lotDisplayId || (await fetchLotDisplayId());
      const lotDevices = await fetchDevicesForLot();
      if (!lotDevices.length) {
        alert("No devices available for this lot.");
        setIsSubmittingEntry(false);
        return;
      }

      const finalTimestamp = timestamp
        ? new Date(timestamp).toISOString()
        : new Date().toISOString();
      await api.post("/alpr/create-alpr", {
        deviceDisplayId: `D-${(resolvedDisplayId || "").replace("PL-", "")}a`,
        displayLotId: resolvedDisplayId,
        laneType: vehicleStatus === "Idle" ? idleLaneType : vehicleStatus,
        distanceChange: vehicleStatus === "Idle" ? 0 : 2,
        timestamp: finalTimestamp,
        plateNumber,
        confidence: 101,
        vehicleMake: "N/A",
        vehicleModel: "N/A",
        vehicleMakeModelConfidence: 0,
        vehicleColor: "N/A",
        vehicleColorConfidence: 0,
        plateColor: "N/A",
        plateCharacterColor: "N/A",
        vehicleState: region,
        vehicleStateConfidence: 0,
        vehicleRegion: "N/A",
        vehicleView: "N/A",
        vehicleViewConfidence: 0,
        fullImage: "N/A",
      });

      refresh();
      setShowManualEntryModal(false);
      setNewEntry({
        plateNumber: "",
        timestamp: new Date().toLocaleString("sv-SE").slice(0, 16),
        region: "Test",
        vehicleStatus: "Enter",
        idleLaneType: "Enter",
      });
    } catch (error: any) {
      console.error("Error creating ALPR entry:", error);
      const message =
        error?.response?.data?.message || error?.message || "Unknown error";
      alert(`Unable to add entry: ${message}`);
    } finally {
      setIsSubmittingEntry(false);
    }
  };

  const handleClearAll = async () => {
    if (!lotId || isClearing) return;
    setIsClearing(true);
    try {
      await api.delete(`/alpr/delete-all-by-lot/${lotId}`);
      setSelectedEntries(new Set());
      refresh();
      setShowClearConfirmModal(false);
    } catch (error: any) {
      console.error("Error clearing ALPR data:", error);
      const message =
        error?.response?.data?.message || error?.message || "Unknown error";
      alert(`Failed to clear ALPR data: ${message}`);
    } finally {
      setIsClearing(false);
    }
  };

  const handleRowClick = (entry: AlprData) => {
    setSelectedEntry(entry);
    setShowDetailModal(true);
  };

  const handleDownloadVehicleData = (entry: AlprData) => {
    const plateNumber = entry.plateNumber || "UnknownPlate";
    const status = entry.vehicleStatus
      ? entry.vehicleStatus.replace(/\s+/g, "_")
      : "UnknownStatus";
    const timestampPart = new Date(entry.timestamp)
      .toISOString()
      .replace(/[:.\-]/g, "")
      .slice(0, 17);
    const filename = `${plateNumber}_${status}_${timestampPart}.csv`;
    const headers = Object.keys(entry);
    const values = Object.values(entry).map((value, index) => {
      const header = headers[index];
      if (header.toLowerCase().includes("timestamp") && typeof value === "string") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleString();
        }
      }
      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }
      if (value === null || value === undefined) {
        return "";
      }
      let stringValue = String(value);
      if (
        stringValue.includes(",") ||
        stringValue.includes('"') ||
        stringValue.includes("\n")
      ) {
        stringValue = `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });

    const csvContent = `${headers.join(",")}\n${values.join(",")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isAllSelected =
    filteredResults.length > 0 &&
    selectedEntries.size === filteredResults.length;
  const selectedCount = selectedEntries.size;

  return (
    <div className="alpr-page">
      <div className="alpr-header">
        <h1>ALPR Log</h1>
      </div>

      <div className="alpr-search-actions">
        <div className="search-bar">
          <img src="/assets/SearchBarIcon.svg" alt="Search" />
          <input
            type="text"
            placeholder="Search plate, region, status, decision..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
        <div className="alpr-action-buttons">
          <button
            type="button"
            className="button-secondary"
            onClick={handleOpenDownloadModal}
            disabled={!filteredResults.length}
          >
            <img src="/assets/Download.svg" alt="Download" />
            <span>
              {selectedCount > 0 && selectedCount < filteredResults.length
                ? `Download (${selectedCount})`
                : "Download All"}
            </span>
          </button>
          <button
            type="button"
            className="button-primary"
            onClick={() => setShowManualEntryModal(true)}
          >
            + Manual Entry
          </button>
        </div>
      </div>

      <div className="table-container app-table-secondary-hover alpr-table-container">
        <table className="app-table alpr-table">
          <thead>
            <tr>
              <th className="alpr-checkbox-column">
                <label className="custom-checkbox" aria-label="Select all entries">
                  <input
                    type="checkbox"
                    checked={isAllSelected && filteredResults.length > 0}
                    onChange={handleSelectAll}
                    className="hidden-checkbox"
                  />
                  <span className="checkbox-styler" />
                </label>
              </th>
              <th onClick={() => handleSort("plateNumber")}>
                Plate
                <img
                  src={
                    sortConfig.key === "plateNumber"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "plateNumber" &&
                    sortConfig.direction === "descending"
                      ? "desc"
                      : ""
                  }`}
                />
              </th>
              <th onClick={() => handleSort("timestamp")}>
                {isMobile ? "Date" : "Date & Time"}
                <img
                  src={
                    sortConfig.key === "timestamp"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "timestamp" &&
                    sortConfig.direction === "descending"
                      ? "desc"
                      : ""
                  }`}
                />
              </th>
              <th onClick={() => handleSort("vehicleStatus")}>
                Status
                <img
                  src={
                    sortConfig.key === "vehicleStatus"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "vehicleStatus" &&
                    sortConfig.direction === "descending"
                      ? "desc"
                      : ""
                  }`}
                />
              </th>
              {!isMobile && (
                <>
                  <th onClick={() => handleSort("decisionReason")}>
                    Decision Reason
                    <img
                      src={
                        sortConfig.key === "decisionReason"
                          ? "/assets/tables/SortArrowSelected.svg"
                          : "/assets/tables/SortArrow.svg"
                      }
                      alt=""
                      className={`sort-arrow ${
                        sortConfig.key === "decisionReason" &&
                        sortConfig.direction === "descending"
                          ? "desc"
                          : ""
                      }`}
                    />
                  </th>
                  <th onClick={() => handleSort("confidence")}>
                    Confidence
                    <img
                      src={
                        sortConfig.key === "confidence"
                          ? "/assets/tables/SortArrowSelected.svg"
                          : "/assets/tables/SortArrow.svg"
                      }
                      alt=""
                      className={`sort-arrow ${
                        sortConfig.key === "confidence" &&
                        sortConfig.direction === "descending"
                          ? "desc"
                          : ""
                      }`}
                    />
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedEntries.map((entry, index) => {
              const checkboxKey = getUniqueKey(entry, index);
              const isChecked = selectedEntries.has(checkboxKey);
              const timestampDisplay = formatTimestampDisplay(entry.timestamp);
              return (
                <tr
                  key={checkboxKey}
                  className="alpr-row"
                  onClick={() => handleRowClick(entry)}
                >
                  <td
                    className="alpr-checkbox-column"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="custom-checkbox" aria-label={`Select ${entry.plateNumber}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCheckboxChange(entry, index)}
                        className="hidden-checkbox"
                      />
                      <span className="checkbox-styler" />
                    </label>
                  </td>
                  <td>
                    <div className="alpr-plate">
                      <span className="plate-badge">{entry.plateNumber}</span>
                    </div>
                  </td>
                  <td>
                    <div className="alpr-date">{timestampDisplay.primary}</div>
                    <div className="alpr-time">{timestampDisplay.secondary}</div>
                  </td>
                  <td>
                    {entry.decisionReason === "ALPR Ignored" ? (
                      <span className="alpr-ignored-chip">IGNORED</span>
                    ) : (
                      <span
                        className={`alpr-status-chip ${entry.vehicleStatus?.toLowerCase()}`}
                      >
                        {entry.vehicleStatus || "N/A"}
                      </span>
                    )}
                  </td>
                  {!isMobile && (
                    <td className="alpr-decision">{entry.decisionReason || "—"}</td>
                  )}
                  {!isMobile && (
                    <td>{typeof entry.confidence === "number" ? `${entry.confidence}%` : "—"}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredResults.length === 0 && !isLoading && (
        <div className="alpr-empty-state">
          <p>No ALPR entries yet</p>
        </div>
      )}

      {isLoading && (
        <div className="alpr-loading">Loading entries...</div>
      )}

      {filteredResults.length > 0 && (
        <div className="app-table-footer alpr-footer">
          <div className="alpr-footer-left">
            <span>
              Showing {paginatedEntries.length} of {totalElements} entries
            </span>
            <button
              type="button"
              className="alpr-clear-link"
              onClick={() => setShowClearConfirmModal(true)}
              disabled={!alprEntries.length}
            >
              Clear All ({totalElements})
            </button>
          </div>
          <Pagination
            currentPage={page}
            totalPages={Math.max(totalPages, 1)}
            totalItems={totalElements}
            itemsOnPage={paginatedEntries.length}
            onPageChange={goToPage}
          />
        </div>
      )}

      <Modal
        isOpen={showManualEntryModal}
        title="Add Manual ALPR Entry"
        confirmText="Add Entry"
        onConfirm={handleAddEntry}
        onCancel={() => setShowManualEntryModal(false)}
        disableConfirm={!newEntry.plateNumber || isSubmittingEntry}
        description="Create a manual ALPR record that will appear in the log immediately."
        customContent={
          <div className="modal-form">
            <label>
              Plate Number
              <input
                value={newEntry.plateNumber}
                onChange={(e) =>
                  setNewEntry((prev) => ({
                    ...prev,
                    plateNumber: e.target.value.toUpperCase(),
                  }))
                }
                placeholder="ABC123"
              />
            </label>
            <label>
              Date & Time
              <input
                type="datetime-local"
                value={newEntry.timestamp}
                onChange={(e) =>
                  setNewEntry((prev) => ({ ...prev, timestamp: e.target.value }))
                }
              />
            </label>
            <label>
              Region / State
              <input
                value={newEntry.region}
                onChange={(e) =>
                  setNewEntry((prev) => ({ ...prev, region: e.target.value }))
                }
                placeholder="Ontario"
              />
            </label>
            <label>
              Vehicle Status
              <Menu
                trigger={
                  <button type="button" className="alpr-menu-trigger">
                    <span>{newEntry.vehicleStatus}</span>
                    <img src="/assets/ArrowDropdown.svg" alt="" />
                  </button>
                }
                items={statusMenuItems}
                placement="bottom-start"
              />
            </label>
            {newEntry.vehicleStatus === "Idle" && (
              <label>
                Lane Type
                <Menu
                  trigger={
                    <button type="button" className="alpr-menu-trigger">
                      <span>{newEntry.idleLaneType}</span>
                      <img src="/assets/ArrowDropdown.svg" alt="" />
                    </button>
                  }
                  items={laneMenuItems}
                  placement="bottom-start"
                />
              </label>
            )}
          </div>
        }
      />

      <Modal
        isOpen={showDownloadModal}
        title="Download ALPR CSV"
        confirmText="Download CSV"
        onConfirm={() => handleDownload(downloadFilename)}
        onCancel={() => setShowDownloadModal(false)}
        disableConfirm={!filteredResults.length}
        customContent={
          <div className="modal-form">
            <label>
              Filename
              <input
                value={downloadFilename}
                onChange={(e) => setDownloadFilename(e.target.value)}
                placeholder="vehicle-log.csv"
              />
            </label>
            <p>
              {selectedCount > 0 && selectedCount < filteredResults.length
                ? `Downloading ${selectedCount} selected entr${
                    selectedCount === 1 ? "y" : "ies"
                  }.`
                : `Downloading all ${filteredResults.length} entr${
                    filteredResults.length === 1 ? "y" : "ies"
                  }.`}
            </p>
          </div>
        }
      />

      <Modal
        isOpen={showClearConfirmModal}
        title="Clear ALPR log"
        description="This removes every ALPR record for this lot. This cannot be undone."
        confirmText="Delete All"
        onConfirm={handleClearAll}
        onCancel={() => setShowClearConfirmModal(false)}
        disableConfirm={isClearing}
      />

      <Modal
        isOpen={showDetailModal && Boolean(selectedEntry)}
        title="Vehicle details"
        onCancel={() => setShowDetailModal(false)}
        hideButtons
        customContent={
          selectedEntry && (
              <div className="alpr-detail">
                <div className="alpr-detail-header">
                  <div className="alpr-detail-header-content">
                    <div className="alpr-detail-plate">
                      <span className="plate-badge">{selectedEntry.plateNumber}</span>
                    </div>
                    <div className="alpr-detail-status-row">
                      {selectedEntry.decisionReason === "ALPR Ignored" ? (
                        <span className="alpr-ignored-chip">IGNORED</span>
                      ) : (
                        <span
                          className={`alpr-status-chip ${selectedEntry.vehicleStatus?.toLowerCase()}`}
                        >
                          {selectedEntry.vehicleStatus || "Status N/A"}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="button-secondary"
                    onClick={() => handleDownloadVehicleData(selectedEntry)}
                >
                  Download record
                </button>
              </div>
              <div className="alpr-detail-grid">
                <div className="alpr-detail-column">
                  <div>
                    <span className="alpr-detail-label">Date & Time:</span>
                    <span>{new Date(selectedEntry.timestamp).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Lane Type:</span>
                    <span>{selectedEntry.laneType || "N/A"}</span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Distance Change:</span>
                    <span>{selectedEntry.distanceChange ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Decision Reason:</span>
                    <span>{selectedEntry.decisionReason || "N/A"}</span>
                  </div>
                </div>
                <div className="alpr-detail-column">
                  <div>
                    <span className="alpr-detail-label">Vehicle Make/Model:</span>
                    <span>
                      {selectedEntry.vehicleMake || "N/A"},{" "}
                      {selectedEntry.vehicleModel || "N/A"} (
                      {selectedEntry.vehicleMakeModelConfidence || 0}%)
                    </span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Vehicle Color:</span>
                    <span>
                      {selectedEntry.vehicleColor || "N/A"} (
                      {selectedEntry.vehicleColorConfidence || 0}%)
                    </span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Plate Colors:</span>
                    <span>
                      {selectedEntry.plateColor || "N/A"} /{" "}
                      {selectedEntry.plateCharacterColor || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Vehicle State:</span>
                    <span>
                      {selectedEntry.vehicleState || "N/A"} (
                      {selectedEntry.vehicleStateConfidence || 0}%)
                    </span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Vehicle Region:</span>
                    <span>{selectedEntry.vehicleRegion || "N/A"}</span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Vehicle View:</span>
                    <span>
                      {selectedEntry.vehicleView || "N/A"} (
                      {selectedEntry.vehicleViewConfidence || 0}%)
                    </span>
                  </div>
                </div>
                <div className="alpr-detail-column alpr-detail-column-muted">
                  <div>
                    <span className="alpr-detail-label">ALPR ID:</span>
                    <span className="alpr-detail-value">
                      <span className="alpr-detail-id">{selectedEntry.alprId || "N/A"}</span>
                      {selectedEntry.alprId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() => handleCopy(selectedEntry.alprId, "alpr-entry-id")}
                          aria-label={
                            copiedKey === "alpr-entry-id" ? "ALPR ID copied" : "Copy ALPR ID"
                          }
                          title={copiedKey === "alpr-entry-id" ? "Copied!" : "Copy ALPR ID"}
                        >
                          <img
                            src={
                              copiedKey === "alpr-entry-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "alpr-entry-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Device ID:</span>
                    <span className="alpr-detail-value">
                      <span className="alpr-detail-id">{selectedEntry.deviceId || "N/A"}</span>
                      {selectedEntry.deviceId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() => handleCopy(selectedEntry.deviceId, "alpr-device-id")}
                          aria-label={
                            copiedKey === "alpr-device-id" ? "Device ID copied" : "Copy Device ID"
                          }
                          title={copiedKey === "alpr-device-id" ? "Copied!" : "Copy Device ID"}
                        >
                          <img
                            src={
                              copiedKey === "alpr-device-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "alpr-device-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="alpr-detail-label">Lot ID:</span>
                    <span className="alpr-detail-value">
                      <span className="alpr-detail-id">{selectedEntry.lotId || "N/A"}</span>
                      {selectedEntry.lotId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() => handleCopy(selectedEntry.lotId, "alpr-lot-id")}
                          aria-label={
                            copiedKey === "alpr-lot-id" ? "Lot ID copied" : "Copy Lot ID"
                          }
                          title={copiedKey === "alpr-lot-id" ? "Copied!" : "Copy Lot ID"}
                        >
                          <img
                            src={
                              copiedKey === "alpr-lot-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "alpr-lot-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {selectedEntry.fullImage && selectedEntry.fullImage !== "N/A" && (
                <div className="alpr-detail-image">
                  <img
                    src={`data:image/jpeg;base64,${selectedEntry.fullImage}`}
                    alt="Vehicle"
                  />
                </div>
              )}
            </div>
          )
        }
      />
    </div>
  );
};

export default ALPR;
